import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowRight, ExternalLink, Calendar, Loader2, RefreshCcw } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsPage = () => {
  const { t, lang } = useLang();
  const copy = t.news;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = `${copy.title} — Food & Beverage Services S.R.L.`;
  }, [copy.title]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/news`, { params: { lang } })
      .then((res) => {
        if (alive) setData(res.data);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [lang]);

  const editorial = data?.editorial;
  const history = data?.history || [];
  const items = data?.items || [];

  return (
    <div className="bg-[#FAFAF8] text-[#1A2421] min-h-screen">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-36 pb-16 lg:pt-44 lg:pb-20 bg-[#F3F3EF]">
          <div className="container-cust">
            <p className="eyebrow mb-5">
              <span className="eyebrow-line"></span>
              {copy.eyebrow}
            </p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A2421] leading-[1.05] tracking-tight max-w-4xl">
              {copy.title}
            </h1>
            <p className="mt-6 text-[#4A5D58] text-base md:text-lg max-w-2xl">
              {copy.subtitle}
            </p>
          </div>
        </section>

        {/* Editorial */}
        <section className="section-pad bg-[#FAFAF8]" data-testid="news-editorial-section">
          <div className="container-cust grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              {loading && (
                <div className="flex items-center gap-3 text-[#4A5D58]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {copy.loading}
                </div>
              )}
              {error && !loading && (
                <div className="text-[#4A5D58]">{copy.error}</div>
              )}
              {!loading && !error && editorial && (
                <article data-testid="news-editorial-article">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#0E5A4E] mb-5">
                    <span>{copy.featured}</span>
                    <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                    <span className="text-[#4A5D58] inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatWeek(editorial.week_start, lang)}
                    </span>
                  </div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1A2421]">
                    {editorial.title}
                  </h2>
                  {editorial.subtitle && (
                    <p className="mt-5 text-lg md:text-xl text-[#4A5D58] italic font-serif-display leading-relaxed">
                      {editorial.subtitle}
                    </p>
                  )}
                  <div className="divider-line my-10 max-w-md" />
                  <div className="prose-fbs">
                    <ReactMarkdown>{editorial.body_markdown}</ReactMarkdown>
                  </div>
                </article>
              )}
              {!loading && !error && !editorial && (
                <div className="text-[#4A5D58]">{copy.editorialUnavailable}</div>
              )}
            </div>

            {/* History sidebar */}
            <aside className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-[#E2E2DB]">
              <div className="sticky top-28">
                <p className="eyebrow mb-5">
                  <span className="eyebrow-line"></span>
                  {copy.archive}
                </p>
                {history.length === 0 && !loading && (
                  <p className="text-sm text-[#4A5D58]">{copy.archiveEmpty}</p>
                )}
                <ul className="space-y-5">
                  {history.map((h) => (
                    <li
                      key={h.id || h.week_key}
                      data-testid={`news-archive-${h.week_key}`}
                      className="group"
                    >
                      <div className="text-[10px] uppercase tracking-widest text-[#4A5D58] mb-1 inline-flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {formatWeek(h.week_start, lang)}
                      </div>
                      <h3 className="font-serif-display text-base text-[#1A2421] group-hover:text-[#0E5A4E] transition-colors leading-snug">
                        {h.title}
                      </h3>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  size="sm"
                  className="mt-8 rounded-full border-[#0E5A4E]/30 text-[#0E5A4E] hover:bg-[#0E5A4E] hover:text-white"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                  {copy.refresh}
                </Button>
              </div>
            </aside>
          </div>
        </section>

        {/* Real news items */}
        <section className="section-pad bg-[#F3F3EF]" data-testid="news-items-section">
          <div className="container-cust">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <p className="eyebrow mb-5">
                  <span className="eyebrow-line"></span>
                  {copy.aggregated}
                </p>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1A2421] leading-tight">
                  {copy.aggregatedTitle}
                </h2>
              </div>
              <p className="text-xs uppercase tracking-widest text-[#4A5D58]">
                {copy.source}: Google News
              </p>
            </div>

            {items.length === 0 ? (
              <p className="text-[#4A5D58]">{copy.noItems}</p>
            ) : (
              <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E2DB]">
                {items.map((it, i) => (
                  <li
                    key={i}
                    data-testid={`news-item-${i}`}
                    className="bg-[#FAFAF8] p-7 lg:p-8 flex flex-col group hover:bg-white transition-colors"
                  >
                    {it.source && (
                      <Badge
                        variant="outline"
                        className="self-start border-[#E2E2DB] text-[10px] uppercase tracking-widest text-[#4A5D58] font-normal mb-4"
                      >
                        {it.source}
                      </Badge>
                    )}
                    <h3 className="font-serif-display text-lg lg:text-xl text-[#1A2421] leading-tight group-hover:text-[#0E5A4E] transition-colors">
                      {it.title}
                    </h3>
                    {it.summary && (
                      <p className="mt-3 text-sm text-[#4A5D58] leading-relaxed line-clamp-4">
                        {it.summary}
                      </p>
                    )}
                    <div className="mt-auto pt-6">
                      <a
                        href={it.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0E5A4E] hover:text-[#0B4A40]"
                        data-testid={`news-item-link-${i}`}
                      >
                        {copy.readMore}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-12 text-xs text-[#4A5D58] max-w-2xl">
              {copy.disclaimer}
            </div>
          </div>
        </section>

        {/* CTA back to home */}
        <section className="py-16 bg-[#FAFAF8]">
          <div className="container-cust flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="font-serif-display text-2xl text-[#1A2421] max-w-xl">
              {copy.ctaText}
            </p>
            <Button
              onClick={() => (window.location.href = "/#quote")}
              className="bg-[#0E5A4E] hover:bg-[#0B4A40] text-white rounded-full px-7 h-12 text-sm"
              data-testid="news-cta-quote"
            >
              {t.nav.requestQuote}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

function formatWeek(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(lang === "it" ? "it-IT" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
}

export default NewsPage;
